import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { api, clearAccessToken, getAccessToken, kontoApi } from "../api";
import type { SessionUser } from "../types";
import * as ws from "../ws-client";

interface AuthContextValue {
  user: SessionUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  /**
   * Erster Schritt. Gibt eine Challenge zurück, wenn die Person die
   * Zwei-Faktor-Anmeldung eingeschaltet hat – dann folgt `loginCode`.
   */
  login: (email: string, password: string) => Promise<{ challenge: string } | null>;
  /** Zweiter Schritt: Code aus der App oder Wiederherstellungscode. */
  loginCode: (challenge: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const refresh = useCallback(async () => {
    if (!getAccessToken()) {
      setUser(null);
      setLoading(false);
      ws.disconnect();
      return;
    }
    try {
      const res = await api.me();
      setUser(res.user ?? null);
      ws.connect();
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.login(email, password);
    if ("two_factor_required" in res) return { challenge: res.challenge };
    setUser(res.user);
    return null;
  }, []);

  const loginCode = useCallback(async (challenge: string, code: string) => {
    const res = await kontoApi.loginZweiFaktor(challenge, code);
    setUser(res.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      clearAccessToken();
    }
    setUser(null);
    ws.disconnect();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, login, loginCode, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
